import { useRef, type ReactNode } from 'react';
import { useStore } from '@nanostores/react';

import { cn } from '@/lib/utils';
import { $leftSidebarOpen, closeLeftSidebar, openLeftSidebar } from '@/shared/stores/ui';

interface LeftHoverSidebarProps {
  /** Content to render inside the sidebar */
  children?: ReactNode;
  /** Width of the sidebar panel (CSS value) */
  width?: string;
  /** Additional CSS classes for the sidebar panel */
  className?: string;
}

/** Delay before opening sidebar on edge hover (ms) */
const HOVER_OPEN_DELAY = 100;
/** Delay before closing sidebar after pointer leaves (ms) */
const HOVER_CLOSE_DELAY = 200;
/** Width of the invisible edge hover zone (px) */
const EDGE_ZONE_WIDTH = 8;

/**
 * LeftHoverSidebar — overlay sidebar that reveals on left-edge hover.
 *
 * Behavior:
 * - Hovering left edge of viewport opens sidebar after short delay
 * - Sidebar auto-closes when pointer leaves the sidebar area
 * - Can also be controlled via $leftSidebarOpen store (for button triggers)
 *
 * @remarks
 * This is a shell-level primitive. Feature content should be passed as children.
 */
export function LeftHoverSidebar({ children, width = '380px', className }: LeftHoverSidebarProps) {
  const isOpen = useStore($leftSidebarOpen);
  const openTimeoutRef = useRef<number | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const clearTimers = () => {
    if (openTimeoutRef.current !== null) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleEdgeEnter = () => {
    clearTimers();
    openTimeoutRef.current = window.setTimeout(() => {
      openLeftSidebar();
    }, HOVER_OPEN_DELAY);
  };

  const handleEdgeLeave = () => {
    // Only clear open timer if sidebar isn't already open
    if (!isOpen && openTimeoutRef.current !== null) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  };

  const handleSidebarEnter = () => {
    // Cancel any pending close when pointer enters sidebar
    clearTimers();
  };

  const handleSidebarLeave = () => {
    clearTimers();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeLeftSidebar();
    }, HOVER_CLOSE_DELAY);
  };

  return (
    <>
      {/* Invisible edge hover zone — triggers sidebar open */}
      <div
        className="fixed left-0 top-0 z-50 h-full"
        style={{ width: `${EDGE_ZONE_WIDTH}px` }}
        onPointerEnter={handleEdgeEnter}
        onPointerLeave={handleEdgeLeave}
        aria-hidden="true"
      />

      {/* Sidebar panel — slides in from left */}
      <aside
        className={cn(
          'fixed left-0 top-1/2 z-50 h-[95vh] -translate-y-1/2',
          'border-r border-sidebar-border/90 bg-sidebar/95 p-3 shadow-xl backdrop-blur-lg',
          'rounded-tr-[18px] rounded-br-[18px]',
          'transform transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          className
        )}
        style={{ width }}
        onPointerEnter={handleSidebarEnter}
        onPointerLeave={handleSidebarLeave}
        aria-label="Navigation sidebar"
      >
        {children}
      </aside>
    </>
  );
}
